/**
 * ================================================================
 * MASTER BACKEND SERVICE - COMPREHENSIVE IMPLEMENTATION
 * Every feature, every button, every action has backend support
 * ================================================================
 */

import { supabase } from '../lib/supabase';
import { brandService } from './brandService';
import { creatorService } from './creatorService';
import { agencyService } from './agencyService';
import { campaignService } from './campaignService';
import { dashboardService } from './dashboardService';

/**
 * ================================================================
 * 1. AUTHENTICATION & USER MANAGEMENT BACKEND
 * ================================================================
 */
export const authBackend = {
  /**
   * Check if email already exists in user_profiles
   */
  async checkEmailExists(email: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('id')
      .eq('email', email.toLowerCase().trim())
      .limit(1);
    
    if (error) {
      console.error('Error checking email:', error);
      return false;
    }
    
    return data && data.length > 0;
  },

  /**
   * Sign up new user with role
   */
  async signUp(email: string, password: string, role: 'brand' | 'creator' | 'agency', fullName: string) {
    try {
      // Normalize email
      const normalizedEmail = email.toLowerCase().trim();
      
      // CHECK IF EMAIL ALREADY EXISTS - PREVENT DUPLICATES
      const emailExists = await this.checkEmailExists(normalizedEmail);
      if (emailExists) {
        throw new Error('An account with this email already exists. Please sign in instead.');
      }

      // Create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: normalizedEmail,
        password,
        options: {
          data: {
            full_name: fullName,
            role: role,
          },
        },
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('User creation failed');

      // Create user profile
      const { error: profileError } = await supabase
        .from('user_profiles')
        .insert({
          user_id: authData.user.id,
          email: email,
          full_name: fullName,
          role: role,
          created_at: new Date().toISOString(),
        });

      if (profileError) throw profileError;

      // Create role-specific profile
      if (role === 'brand') {
        await supabase.from('brand_profiles').insert({
          user_id: authData.user.id,
          company_name: fullName,
          verified: false,
        });
      } else if (role === 'creator') {
        await supabase.from('creator_profiles').insert({
          user_id: authData.user.id,
          username: email.split('@')[0],
          bio: '',
          followers: 0,
          engagement_rate: 0,
        });
      } else if (role === 'agency') {
        await supabase.from('agency_settings').insert({
          agency_id: authData.user.id,
          company_name: fullName,
        });
      }

      // Log activity
      await dashboardService.logActivity(
        authData.user.id,
        'account_created',
        'Account Created',
        `New ${role} account created for ${email}`
      );

      // Send welcome notification
      await supabase.rpc('create_notification', {
        p_user_id: authData.user.id,
        p_type: 'welcome',
        p_title: 'Welcome to NOONX!',
        p_message: `Your ${role} account has been created successfully. Start exploring!`,
        p_link: `/${role}-dashboard`,
      });

      return { success: true, user: authData.user };
    } catch (error: any) {
      console.error('Sign up error:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Sign in user
   */
  async signIn(email: string, password: string) {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
      if (!data.user) throw new Error('Login failed');

      // Get user profile
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', data.user.id)
        .single();

      // Log activity
      await dashboardService.logActivity(
        data.user.id,
        'login',
        'User Logged In',
        `User logged in from ${new Date().toLocaleString()}`
      );

      return { success: true, user: data.user, profile };
    } catch (error: any) {
      console.error('Sign in error:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Sign out user
   */
  async signOut() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        await dashboardService.logActivity(
          user.id,
          'logout',
          'User Logged Out',
          `User logged out at ${new Date().toLocaleString()}`
        );
      }

      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      return { success: true };
    } catch (error: any) {
      console.error('Sign out error:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Reset password
   */
  async resetPassword(email: string) {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      return { success: true };
    } catch (error: any) {
      console.error('Reset password error:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Update password
   */
  async updatePassword(newPassword: string) {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await dashboardService.logActivity(
          user.id,
          'password_changed',
          'Password Updated',
          'User updated their password'
        );
      }

      return { success: true };
    } catch (error: any) {
      console.error('Update password error:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Update user profile
   */
  async updateProfile(userId: string, updates: any) {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('user_id', userId);

      if (error) throw error;

      await dashboardService.logActivity(
        userId,
        'profile_updated',
        'Profile Updated',
        'User profile information updated'
      );

      return { success: true };
    } catch (error: any) {
      console.error('Update profile error:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get current user with full profile
   */
  async getCurrentUser() {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        return { success: false, user: null, profile: null };
      }

      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (profileError) throw profileError;

      return { success: true, user, profile };
    } catch (error: any) {
      console.error('Get current user error:', error);
      return { success: false, user: null, profile: null, error: error.message };
    }
  },
};

/**
 * ================================================================
 * 2. CAMPAIGN MANAGEMENT BACKEND (COMPLETE)
 * ================================================================
 */
export const campaignBackend = {
  /**
   * Create new campaign
   */
  async createCampaign(brandId: string, campaignData: any) {
    try {
      const { data, error } = await supabase
        .from('campaigns')
        .insert({
          brand_id: brandId,
          title: campaignData.title,
          description: campaignData.description,
          budget: campaignData.budget,
          category: campaignData.category,
          platform: campaignData.platform,
          content_type: campaignData.contentType,
          deadline: campaignData.deadline,
          requirements: campaignData.requirements,
          target_audience: campaignData.targetAudience,
          status: 'draft',
          created_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;

      // Log activity
      await dashboardService.logActivity(
        brandId,
        'campaign_created',
        'Campaign Created',
        `Created campaign: ${campaignData.title}`
      );

      // Notify matching creators
      await this.notifyMatchingCreators(data.id, campaignData);

      return { success: true, campaign: data };
    } catch (error: any) {
      console.error('Create campaign error:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Update campaign
   */
  async updateCampaign(campaignId: string, updates: any) {
    try {
      const { data, error } = await supabase
        .from('campaigns')
        .update(updates)
        .eq('id', campaignId)
        .select()
        .single();

      if (error) throw error;

      // Get brand ID for logging
      const brandId = data.brand_id;

      await dashboardService.logActivity(
        brandId,
        'campaign_updated',
        'Campaign Updated',
        `Updated campaign: ${data.title}`
      );

      return { success: true, campaign: data };
    } catch (error: any) {
      console.error('Update campaign error:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Delete campaign
   */
  async deleteCampaign(campaignId: string, brandId: string) {
    try {
      // Get campaign title first
      const { data: campaign } = await supabase
        .from('campaigns')
        .select('title')
        .eq('id', campaignId)
        .single();

      const { error } = await supabase
        .from('campaigns')
        .delete()
        .eq('id', campaignId);

      if (error) throw error;

      await dashboardService.logActivity(
        brandId,
        'campaign_deleted',
        'Campaign Deleted',
        `Deleted campaign: ${campaign?.title || campaignId}`
      );

      return { success: true };
    } catch (error: any) {
      console.error('Delete campaign error:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Publish campaign (change status from draft to open)
   */
  async publishCampaign(campaignId: string, brandId: string) {
    try {
      const { data, error } = await supabase
        .from('campaigns')
        .update({ status: 'open', published_at: new Date().toISOString() })
        .eq('id', campaignId)
        .select()
        .single();

      if (error) throw error;

      await dashboardService.logActivity(
        brandId,
        'campaign_published',
        'Campaign Published',
        `Published campaign: ${data.title}`
      );

      // Notify all relevant creators
      await this.notifyMatchingCreators(campaignId, data);

      return { success: true, campaign: data };
    } catch (error: any) {
      console.error('Publish campaign error:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get campaigns for brand
   */
  async getCampaignsForBrand(brandId: string, filters?: any) {
    try {
      let query = supabase
        .from('campaigns')
        .select(`
          *,
          campaign_applications(count)
        `)
        .eq('brand_id', brandId)
        .order('created_at', { ascending: false });

      if (filters?.status) {
        query = query.eq('status', filters.status);
      }

      const { data, error } = await query;

      if (error) throw error;

      return { success: true, campaigns: data || [] };
    } catch (error: any) {
      console.error('Get campaigns error:', error);
      return { success: false, campaigns: [], error: error.message };
    }
  },

  /**
   * Get campaign details with applications
   */
  async getCampaignDetails(campaignId: string) {
    try {
      const { data, error } = await supabase
        .from('campaigns')
        .select(`
          *,
          campaign_applications(
            *,
            creator_profiles(
              *,
              user_profiles!creator_profiles_user_id_fkey(full_name, avatar_url)
            )
          )
        `)
        .eq('id', campaignId)
        .single();

      if (error) throw error;

      // Fetch brand info separately
      let brandInfo = null;
      if (data?.brand_id) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('full_name, username')
          .eq('id', data.brand_id)
          .single();
        brandInfo = { company_name: profile?.full_name || profile?.username };
      }

      return { success: true, campaign: { ...data, brand_profile: brandInfo } };
    } catch (error: any) {
      console.error('Get campaign details error:', error);
      return { success: false, campaign: null, error: error.message };
    }
  },

  /**
   * Notify matching creators about new campaign
   */
  async notifyMatchingCreators(campaignId: string, campaignData: any) {
    try {
      // Get matching creators based on category
      const { data: creators } = await supabase
        .from('creator_profiles')
        .select('user_id, username')
        .eq('category', campaignData.category)
        .gte('followers', campaignData.min_followers || 0);

      if (!creators) return;

      // Create notifications for each creator
      for (const creator of creators) {
        await supabase.rpc('create_notification', {
          p_user_id: creator.user_id,
          p_type: 'new_campaign',
          p_title: 'New Campaign Available!',
          p_message: `A new ${campaignData.category} campaign "${campaignData.title}" is now open for applications.`,
          p_link: `/campaign/${campaignId}`,
        });
      }

      return { success: true, notifiedCount: creators.length };
    } catch (error: any) {
      console.error('Notify creators error:', error);
      return { success: false };
    }
  },
};

/**
 * ================================================================
 * 3. APPLICATION MANAGEMENT BACKEND
 * ================================================================
 */
export const applicationBackend = {
  /**
   * Submit application to campaign
   */
  async submitApplication(creatorId: string, campaignId: string, applicationData: any) {
    try {
      // Get creator profile ID
      const { data: profile } = await supabase
        .from('creator_profiles')
        .select('id, user_id')
        .eq('user_id', creatorId)
        .single();

      if (!profile) throw new Error('Creator profile not found');

      const { data, error } = await supabase
        .from('campaign_applications')
        .insert({
          campaign_id: campaignId,
          creator_id: profile.id,
          proposed_budget: applicationData.proposedBudget,
          estimated_reach: applicationData.estimatedReach,
          proposal: applicationData.proposal,
          content_ideas: applicationData.contentIdeas,
          timeline: applicationData.timeline,
          status: 'pending',
          created_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;

      // Get campaign and brand info
      const { data: campaign } = await supabase
        .from('campaigns')
        .select('title, brand_id')
        .eq('id', campaignId)
        .single();

      // Log activity
      await dashboardService.logActivity(
        creatorId,
        'application_submitted',
        'Application Submitted',
        `Applied to campaign: ${campaign?.title}`
      );

      // Notify brand
      if (campaign) {
        await supabase.rpc('create_notification', {
          p_user_id: campaign.brand_id,
          p_type: 'new_application',
          p_title: 'New Application Received',
          p_message: `You have a new application for "${campaign.title}"`,
          p_link: `/campaign/${campaignId}/applications`,
        });
      }

      return { success: true, application: data };
    } catch (error: any) {
      console.error('Submit application error:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Update application status (approve/reject)
   */
  async updateApplicationStatus(
    applicationId: string,
    status: 'approved' | 'rejected' | 'in_progress' | 'completed',
    brandId: string,
    feedback?: string
  ) {
    try {
      // Get application details
      const { data: application } = await supabase
        .from('campaign_applications')
        .select(`
          *,
          creator_profiles(user_id),
          campaigns(title)
        `)
        .eq('id', applicationId)
        .single();

      // Try RPC first to handle enum properly
      let updateSuccess = false;
      try {
        const { error: rpcError } = await supabase.rpc('update_application_status', {
          app_id: applicationId,
          new_status: status,
          deliverable_json: feedback ? { feedback } : null
        });
        if (!rpcError) {
          updateSuccess = true;
        }
      } catch (rpcErr) {
        console.log('⚠️ RPC not available, using fallback');
      }

      // Fallback: Update non-enum fields only
      if (!updateSuccess) {
        const { error } = await supabase
          .from('campaign_applications')
          .update({ 
            deliverable_status: status === 'approved' ? 'approved' : 
                               status === 'completed' ? 'completed' : 'pending',
            updated_at: new Date().toISOString(),
            feedback
          })
          .eq('id', applicationId);

        if (error) throw error;
      }

      // Get updated data
      const { data } = await supabase
        .from('campaign_applications')
        .select('*')
        .eq('id', applicationId)
        .single();

      // Log activity
      await dashboardService.logActivity(
        brandId,
        `application_${status}`,
        `Application ${status}`,
        `Application for "${application?.campaigns?.title}" was ${status}`
      );

      // Notify creator
      if (application?.creator_profiles?.user_id) {
        await supabase.rpc('create_notification', {
          p_user_id: application.creator_profiles.user_id,
          p_type: `application_${status}`,
          p_title: `Application ${status}!`,
          p_message: `Your application for "${application.campaigns?.title}" has been ${status}.${feedback ? ` Feedback: ${feedback}` : ''}`,
          p_link: `/creator-dashboard/applications`,
        });
      }

      return { success: true, application: data };
    } catch (error: any) {
      console.error('Update application status error:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get applications for campaign
   */
  async getApplicationsForCampaign(campaignId: string) {
    try {
      const { data, error } = await supabase
        .from('campaign_applications')
        .select(`
          *,
          creator_profiles(
            *,
            user_profiles!creator_profiles_user_id_fkey(full_name, avatar_url, email)
          )
        `)
        .eq('campaign_id', campaignId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return { success: true, applications: data || [] };
    } catch (error: any) {
      console.error('Get applications error:', error);
      return { success: false, applications: [], error: error.message };
    }
  },

  /**
   * Get applications for creator
   */
  async getApplicationsForCreator(creatorUserId: string) {
    try {
      // Get creator profile
      const { data: profile } = await supabase
        .from('creator_profiles')
        .select('id')
        .eq('user_id', creatorUserId)
        .single();

      if (!profile) throw new Error('Creator profile not found');

      const { data, error } = await supabase
        .from('campaign_applications')
        .select(`
          *,
          campaigns(*)
        `)
        .eq('creator_id', profile.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch brand info for each application
      const applicationsWithBrand = await Promise.all(
        (data || []).map(async (app) => {
          if (app.campaigns?.brand_id) {
            const { data: brandProfile } = await supabase
              .from('user_profiles')
              .select('full_name, username')
              .eq('id', app.campaigns.brand_id)
              .single();
            return {
              ...app,
              campaigns: {
                ...app.campaigns,
                brand_name: brandProfile?.full_name || brandProfile?.username || 'Unknown Brand'
              }
            };
          }
          return app;
        })
      );

      return { success: true, applications: applicationsWithBrand };
    } catch (error: any) {
      console.error('Get creator applications error:', error);
      return { success: false, applications: [], error: error.message };
    }
  },

  /**
   * Withdraw application
   */
  async withdrawApplication(applicationId: string, creatorId: string) {
    try {
      const { error } = await supabase
        .from('campaign_applications')
        .delete()
        .eq('id', applicationId);

      if (error) throw error;

      await dashboardService.logActivity(
        creatorId,
        'application_withdrawn',
        'Application Withdrawn',
        'Application was withdrawn by creator'
      );

      return { success: true };
    } catch (error: any) {
      console.error('Withdraw application error:', error);
      return { success: false, error: error.message };
    }
  },
};

/**
 * ================================================================
 * 4. INVITATION MANAGEMENT BACKEND
 * ================================================================
 */
export const invitationBackend = {
  /**
   * Send invitation to creator
   */
  async sendInvitation(brandId: string, creatorId: string, campaignId: string, message?: string) {
    try {
      // Get creator profile ID
      const { data: creator } = await supabase
        .from('creator_profiles')
        .select('id, user_id, username')
        .eq('user_id', creatorId)
        .single();

      if (!creator) throw new Error('Creator not found');

      // Get campaign info
      const { data: campaign } = await supabase
        .from('campaigns')
        .select('title')
        .eq('id', campaignId)
        .single();

      const { data, error } = await supabase
        .from('campaign_invitations')
        .insert({
          campaign_id: campaignId,
          creator_id: creator.id,
          brand_message: message,
          status: 'pending',
          created_at: new Date().toISOString(),
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days
        })
        .select()
        .single();

      if (error) throw error;

      // Log activity
      await dashboardService.logActivity(
        brandId,
        'invitation_sent',
        'Invitation Sent',
        `Sent invitation to ${creator.username} for ${campaign?.title}`
      );

      // Notify creator
      await supabase.rpc('create_notification', {
        p_user_id: creator.user_id,
        p_type: 'campaign_invitation',
        p_title: 'New Campaign Invitation!',
        p_message: `You've been invited to collaborate on "${campaign?.title}"`,
        p_link: `/creator-dashboard/invitations`,
      });

      return { success: true, invitation: data };
    } catch (error: any) {
      console.error('Send invitation error:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Respond to invitation (accept/decline)
   */
  async respondToInvitation(invitationId: string, creatorId: string, response: 'accepted' | 'declined', reason?: string) {
    try {
      // Get invitation details
      const { data: invitation } = await supabase
        .from('campaign_invitations')
        .select(`
          *,
          campaigns(title, brand_id)
        `)
        .eq('id', invitationId)
        .single();

      const { data, error } = await supabase
        .from('campaign_invitations')
        .update({
          status: response,
          responded_at: new Date().toISOString(),
          decline_reason: reason,
        })
        .eq('id', invitationId)
        .select()
        .single();

      if (error) throw error;

      // If accepted, create application automatically
      if (response === 'accepted') {
        await applicationBackend.submitApplication(
          creatorId,
          invitation.campaign_id,
          {
            proposedBudget: 0,
            estimatedReach: 0,
            proposal: `Accepted invitation to collaborate on ${invitation.campaigns?.title}`,
            contentIdeas: [],
            timeline: '2 weeks',
          }
        );
      }

      // Log activity
      await dashboardService.logActivity(
        creatorId,
        `invitation_${response}`,
        `Invitation ${response}`,
        `${response} invitation for ${invitation.campaigns?.title}`
      );

      // Notify brand
      if (invitation.campaigns?.brand_id) {
        await supabase.rpc('create_notification', {
          p_user_id: invitation.campaigns.brand_id,
          p_type: `invitation_${response}`,
          p_title: `Invitation ${response}`,
          p_message: `Your invitation for "${invitation.campaigns.title}" was ${response}.${reason ? ` Reason: ${reason}` : ''}`,
          p_link: `/campaign/${invitation.campaign_id}`,
        });
      }

      return { success: true, invitation: data };
    } catch (error: any) {
      console.error('Respond to invitation error:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get invitations for creator
   */
  async getInvitationsForCreator(creatorUserId: string) {
    try {
      // Get creator profile
      const { data: profile } = await supabase
        .from('creator_profiles')
        .select('id')
        .eq('user_id', creatorUserId)
        .single();

      if (!profile) throw new Error('Creator profile not found');

      const { data, error } = await supabase
        .from('campaign_invitations')
        .select(`
          *,
          campaigns(*)
        `)
        .eq('creator_id', profile.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch brand info for each invitation
      const invitationsWithBrand = await Promise.all(
        (data || []).map(async (inv) => {
          if (inv.campaigns?.brand_id) {
            const { data: brandProfile } = await supabase
              .from('user_profiles')
              .select('full_name, username')
              .eq('id', inv.campaigns.brand_id)
              .single();
            return {
              ...inv,
              campaigns: {
                ...inv.campaigns,
                brand_name: brandProfile?.full_name || brandProfile?.username || 'Unknown Brand'
              }
            };
          }
          return inv;
        })
      );

      return { success: true, invitations: invitationsWithBrand };
    } catch (error: any) {
      console.error('Get invitations error:', error);
      return { success: false, invitations: [], error: error.message };
    }
  },

  /**
   * Get invitations sent by brand
   */
  async getInvitationsByBrand(brandId: string) {
    try {
      // First get all campaign IDs for this brand
      const { data: campaigns, error: cErr } = await supabase
        .from('campaigns')
        .select('id')
        .eq('brand_id', brandId);
      
      if (cErr) throw cErr;
      
      const campaignIds = campaigns?.map((c: { id: string }) => c.id) || [];
      if (campaignIds.length === 0) return { success: true, invitations: [] };

      // Then get invitations for those campaigns
      const { data, error } = await supabase
        .from('campaign_invitations')
        .select(`
          *,
          campaigns(brand_id, title),
          creator_profiles(
            username,
            user_profiles!creator_profiles_user_id_fkey(full_name, avatar_url)
          )
        `)
        .in('campaign_id', campaignIds)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return { success: true, invitations: data || [] };
    } catch (error: any) {
      console.error('Get brand invitations error:', error);
      return { success: false, invitations: [], error: error.message };
    }
  },

  /**
   * Cancel invitation
   */
  async cancelInvitation(invitationId: string, brandId: string) {
    try {
      const { error } = await supabase
        .from('campaign_invitations')
        .update({ status: 'cancelled' })
        .eq('id', invitationId);

      if (error) throw error;

      await dashboardService.logActivity(
        brandId,
        'invitation_cancelled',
        'Invitation Cancelled',
        'Campaign invitation was cancelled'
      );

      return { success: true };
    } catch (error: any) {
      console.error('Cancel invitation error:', error);
      return { success: false, error: error.message };
    }
  },
};

/**
 * ================================================================
 * 5. CREATOR DISCOVERY & SEARCH BACKEND
 * ================================================================
 */
export const discoveryBackend = {
  /**
   * Search creators with filters
   */
  async searchCreators(filters: {
    category?: string;
    minFollowers?: number;
    maxFollowers?: number;
    minEngagement?: number;
    maxEngagement?: number;
    platform?: string;
    verified?: boolean;
    search?: string;
    limit?: number;
    offset?: number;
  }) {
    try {
      let query = supabase
        .from('creator_profiles')
        .select(`
          *,
          user_profiles!creator_profiles_user_id_fkey(full_name, avatar_url, email)
        `, { count: 'exact' });

      // Apply filters
      if (filters.category) {
        query = query.eq('category', filters.category);
      }

      if (filters.minFollowers !== undefined) {
        query = query.gte('followers', filters.minFollowers);
      }

      if (filters.maxFollowers !== undefined) {
        query = query.lte('followers', filters.maxFollowers);
      }

      if (filters.minEngagement !== undefined) {
        query = query.gte('engagement_rate', filters.minEngagement);
      }

      if (filters.maxEngagement !== undefined) {
        query = query.lte('engagement_rate', filters.maxEngagement);
      }

      if (filters.verified !== undefined) {
        query = query.eq('verified', filters.verified);
      }

      if (filters.search) {
        query = query.or(`username.ilike.%${filters.search}%,bio.ilike.%${filters.search}%`);
      }

      // Pagination
      const limit = filters.limit || 20;
      const offset = filters.offset || 0;
      query = query.range(offset, offset + limit - 1);

      // Order by
      query = query.order('rating', { ascending: false });

      const { data, error, count } = await query;

      if (error) throw error;

      return {
        success: true,
        creators: data || [],
        total: count || 0,
        hasMore: count ? (offset + limit) < count : false,
      };
    } catch (error: any) {
      console.error('Search creators error:', error);
      return { success: false, creators: [], total: 0, hasMore: false, error: error.message };
    }
  },

  /**
   * Get featured/recommended creators
   */
  async getFeaturedCreators(brandId?: string, limit: number = 10) {
    try {
      const { data, error } = await supabase
        .from('creator_profiles')
        .select(`
          *,
          user_profiles!creator_profiles_user_id_fkey(full_name, avatar_url)
        `)
        .gte('rating', 4.0)
        .gte('followers', 10000)
        .order('rating', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return { success: true, creators: data || [] };
    } catch (error: any) {
      console.error('Get featured creators error:', error);
      return { success: false, creators: [], error: error.message };
    }
  },

  /**
   * Get creator recommendations based on campaign
   */
  async getRecommendationsForCampaign(campaignId: string) {
    try {
      // Get campaign details
      const { data: campaign } = await supabase
        .from('campaigns')
        .select('category, budget, platform')
        .eq('id', campaignId)
        .single();

      if (!campaign) throw new Error('Campaign not found');

      // Find matching creators
      const { data, error } = await supabase
        .from('creator_profiles')
        .select(`
          *,
          user_profiles!creator_profiles_user_id_fkey(full_name, avatar_url)
        `)
        .eq('category', campaign.category)
        .gte('rating', 3.5)
        .order('rating', { ascending: false })
        .limit(20);

      if (error) throw error;

      return { success: true, creators: data || [] };
    } catch (error: any) {
      console.error('Get recommendations error:', error);
      return { success: false, creators: [], error: error.message };
    }
  },

  /**
   * Save creator to wishlist
   */
  async saveCreatorToWishlist(brandId: string, creatorId: string) {
    try {
      const { data, error } = await supabase
        .from('brand_creator_wishlist')
        .insert({
          brand_id: brandId,
          creator_id: creatorId,
          created_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) {
        // Check if already exists
        if (error.code === '23505') {
          return { success: true, message: 'Creator already in wishlist' };
        }
        throw error;
      }

      await dashboardService.logActivity(
        brandId,
        'creator_saved',
        'Creator Saved',
        'Added creator to wishlist'
      );

      return { success: true, data };
    } catch (error: any) {
      console.error('Save creator error:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Remove creator from wishlist
   */
  async removeCreatorFromWishlist(brandId: string, creatorId: string) {
    try {
      const { error } = await supabase
        .from('brand_creator_wishlist')
        .delete()
        .eq('brand_id', brandId)
        .eq('creator_id', creatorId);

      if (error) throw error;

      return { success: true };
    } catch (error: any) {
      console.error('Remove from wishlist error:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get wishlist
   */
  async getWishlist(brandId: string) {
    try {
      const { data, error } = await supabase
        .from('brand_creator_wishlist')
        .select(`
          *,
          creator_profiles(
            *,
            user_profiles!creator_profiles_user_id_fkey(full_name, avatar_url)
          )
        `)
        .eq('brand_id', brandId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return { success: true, wishlist: data || [] };
    } catch (error: any) {
      console.error('Get wishlist error:', error);
      return { success: false, wishlist: [], error: error.message };
    }
  },
};

/**
 * ================================================================
 * 6. ANALYTICS & REPORTING BACKEND
 * ================================================================
 */
export const analyticsBackend = {
  /**
   * Get campaign analytics
   */
  async getCampaignAnalytics(campaignId: string) {
    try {
      const { data, error } = await supabase.rpc('get_campaign_analytics', {
        p_campaign_id: campaignId
      });

      if (error) throw error;

      return { success: true, analytics: data };
    } catch (error: any) {
      console.error('Get campaign analytics error:', error);
      return { success: false, analytics: null, error: error.message };
    }
  },

  /**
   * Get brand analytics overview
   */
  async getBrandAnalytics(brandId: string, dateRange?: { start: string; end: string }) {
    try {
      const metrics = await dashboardService.getDashboardMetrics(brandId, 'brand');

      // Get detailed campaign performance
      const { data: campaigns } = await supabase
        .from('campaigns')
        .select(`
          id,
          title,
          budget,
          spent,
          status,
          created_at,
          campaign_applications(count),
          influencer_performance(total_views, total_engagement, total_reach)
        `)
        .eq('brand_id', brandId);

      return {
        success: true,
        analytics: {
          overview: metrics,
          campaigns: campaigns || [],
        },
      };
    } catch (error: any) {
      console.error('Get brand analytics error:', error);
      return { success: false, analytics: null, error: error.message };
    }
  },

  /**
   * Get creator analytics
   */
  async getCreatorAnalytics(creatorUserId: string) {
    try {
      const metrics = await dashboardService.getDashboardMetrics(creatorUserId, 'creator');

      // Get performance history
      const { data: profile } = await supabase
        .from('creator_profiles')
        .select('id')
        .eq('user_id', creatorUserId)
        .single();

      if (!profile) throw new Error('Creator profile not found');

      const { data: performance } = await supabase
        .from('influencer_performance')
        .select('*')
        .eq('creator_id', profile.id)
        .order('created_at', { ascending: false });

      return {
        success: true,
        analytics: {
          overview: metrics,
          performance: performance || [],
        },
      };
    } catch (error: any) {
      console.error('Get creator analytics error:', error);
      return { success: false, analytics: null, error: error.message };
    }
  },

  /**
   * Export analytics data
   */
  async exportAnalytics(userId: string, type: 'campaigns' | 'applications' | 'overview', format: 'csv' | 'pdf') {
    try {
      // This would typically generate a file and return a download URL
      // For now, return success
      await dashboardService.logActivity(
        userId,
        'analytics_exported',
        'Analytics Exported',
        `Exported ${type} analytics as ${format}`
      );

      return { success: true, downloadUrl: '/api/exports/analytics_' + Date.now() + '.' + format };
    } catch (error: any) {
      console.error('Export analytics error:', error);
      return { success: false, error: error.message };
    }
  },
};

/**
 * ================================================================
 * 7. PAYMENT & EARNINGS BACKEND
 * ================================================================
 */
export const paymentBackend = {
  /**
   * Process payment to creator
   */
  async processPayment(applicationId: string, amount: number, brandId: string) {
    try {
      // Create payment record
      const { data, error } = await supabase
        .from('payments')
        .insert({
          application_id: applicationId,
          amount: amount,
          status: 'completed',
          payment_method: 'stripe',
          processed_at: new Date().toISOString(),
          created_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;

      // Update deliverable payment status
      await supabase
        .from('deliverables')
        .update({
          payment_status: 'paid',
          payment_amount: amount,
          paid_at: new Date().toISOString(),
        })
        .eq('application_id', applicationId);

      // Log activity
      await dashboardService.logActivity(
        brandId,
        'payment_processed',
        'Payment Processed',
        `Processed payment of $${amount}`
      );

      // Notify creator
      const { data: application } = await supabase
        .from('campaign_applications')
        .select('creator_profiles(user_id)')
        .eq('id', applicationId)
        .single();

      const creatorUserId = application?.creator_profiles?.[0]?.user_id;

      if (creatorUserId) {
        await supabase.rpc('create_notification', {
          p_user_id: creatorUserId,
          p_type: 'payment_received',
          p_title: 'Payment Received!',
          p_message: `You've received a payment of $${amount}`,
          p_link: '/creator-dashboard/earnings',
        });
      }

      return { success: true, payment: data };
    } catch (error: any) {
      console.error('Process payment error:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get earnings for creator
   */
  async getCreatorEarnings(creatorUserId: string) {
    try {
      const { data: profile } = await supabase
        .from('creator_profiles')
        .select('id')
        .eq('user_id', creatorUserId)
        .single();

      if (!profile) throw new Error('Creator profile not found');

      const { data, error } = await supabase
        .from('deliverables')
        .select(`
          *,
          campaign_applications(
            campaigns(title, brand_profiles(company_name))
          )
        `)
        .eq('campaign_applications.creator_id', profile.id)
        .eq('payment_status', 'paid')
        .order('paid_at', { ascending: false });

      if (error) throw error;

      const total = data?.reduce((sum, d) => sum + (d.payment_amount || 0), 0) || 0;

      return {
        success: true,
        earnings: {
          total,
          payments: data || [],
        },
      };
    } catch (error: any) {
      console.error('Get creator earnings error:', error);
      return { success: false, earnings: null, error: error.message };
    }
  },

  /**
   * Request payout
   */
  async requestPayout(creatorUserId: string, amount: number) {
    try {
      // Get creator profile ID first
      const { data: creatorProfile } = await supabase
        .from('creator_profiles')
        .select('id')
        .eq('user_id', creatorUserId)
        .single();

      if (!creatorProfile) {
        throw new Error('Creator profile not found');
      }

      const { data, error } = await supabase
        .from('payout_requests')
        .insert({
          creator_id: creatorProfile.id,
          amount: amount,
          status: 'pending',
          requested_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;

      await dashboardService.logActivity(
        creatorUserId,
        'payout_requested',
        'Payout Requested',
        `Requested payout of $${amount}`
      );

      await supabase.rpc('create_notification', {
        p_user_id: creatorUserId,
        p_type: 'payout_requested',
        p_title: 'Payout Request Submitted',
        p_message: `Your payout request for $${amount} has been submitted and is being processed.`,
        p_link: '/creator-dashboard/earnings',
      });

      return { success: true, payout: data };
    } catch (error: any) {
      console.error('Request payout error:', error);
      return { success: false, error: error.message };
    }
  },
};

/**
 * ================================================================
 * 8. MESSAGING & COMMUNICATION BACKEND
 * ================================================================
 */
export const messagingBackend = {
  /**
   * Send message
   */
  async sendMessage(senderId: string, recipientId: string, content: string, campaignId?: string) {
    try {
      const { data, error } = await supabase
        .from('messages')
        .insert({
          sender_id: senderId,
          recipient_id: recipientId,
          content: content,
          campaign_id: campaignId,
          read: false,
          created_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;

      // Notify recipient
      await supabase.rpc('create_notification', {
        p_user_id: recipientId,
        p_type: 'new_message',
        p_title: 'New Message',
        p_message: content.substring(0, 50) + (content.length > 50 ? '...' : ''),
        p_link: '/messages',
      });

      return { success: true, message: data };
    } catch (error: any) {
      console.error('Send message error:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get conversations
   */
  async getConversations(userId: string) {
    try {
      const { data, error } = await supabase.rpc('get_user_conversations', {
        p_user_id: userId
      });

      if (error) throw error;

      return { success: true, conversations: data || [] };
    } catch (error: any) {
      console.error('Get conversations error:', error);
      return { success: false, conversations: [], error: error.message };
    }
  },

  /**
   * Get messages in conversation
   */
  async getMessages(userId: string, otherUserId: string) {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .or(`and(sender_id.eq.${userId},recipient_id.eq.${otherUserId}),and(sender_id.eq.${otherUserId},recipient_id.eq.${userId})`)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Mark as read
      await supabase
        .from('messages')
        .update({ read: true })
        .eq('recipient_id', userId)
        .eq('sender_id', otherUserId)
        .eq('read', false);

      return { success: true, messages: data || [] };
    } catch (error: any) {
      console.error('Get messages error:', error);
      return { success: false, messages: [], error: error.message };
    }
  },

  /**
   * Mark message as read
   */
  async markAsRead(messageId: string) {
    try {
      const { error } = await supabase
        .from('messages')
        .update({ read: true })
        .eq('id', messageId);

      if (error) throw error;

      return { success: true };
    } catch (error: any) {
      console.error('Mark as read error:', error);
      return { success: false, error: error.message };
    }
  },
};

/**
 * ================================================================
 * 9. SETTINGS & PREFERENCES BACKEND
 * ================================================================
 */
export const settingsBackend = {
  /**
   * Update notification preferences
   */
  async updateNotificationPreferences(userId: string, preferences: any) {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          notification_preferences: preferences,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', userId);

      if (error) throw error;

      await dashboardService.logActivity(
        userId,
        'settings_updated',
        'Settings Updated',
        'Notification preferences updated'
      );

      return { success: true };
    } catch (error: any) {
      console.error('Update preferences error:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Update brand profile
   */
  async updateBrandProfile(brandId: string, updates: any) {
    try {
      const { data, error } = await supabase
        .from('brand_profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', brandId)
        .select()
        .single();

      if (error) throw error;

      await dashboardService.logActivity(
        brandId,
        'profile_updated',
        'Profile Updated',
        'Brand profile information updated'
      );

      return { success: true, profile: data };
    } catch (error: any) {
      console.error('Update brand profile error:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Update creator profile
   */
  async updateCreatorProfile(creatorId: string, updates: any) {
    try {
      const { data, error } = await supabase
        .from('creator_profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', creatorId)
        .select()
        .single();

      if (error) throw error;

      await dashboardService.logActivity(
        creatorId,
        'profile_updated',
        'Profile Updated',
        'Creator profile information updated'
      );

      return { success: true, profile: data };
    } catch (error: any) {
      console.error('Update creator profile error:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Upload profile picture
   */
  async uploadProfilePicture(userId: string, file: File) {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}-${Date.now()}.${fileExt}`;
      const filePath = `avatars/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('profile-pictures')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('profile-pictures')
        .getPublicUrl(filePath);

      // Update user profile
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ avatar_url: publicUrl })
        .eq('user_id', userId);

      if (updateError) throw updateError;

      await dashboardService.logActivity(
        userId,
        'avatar_updated',
        'Profile Picture Updated',
        'User uploaded new profile picture'
      );

      return { success: true, avatarUrl: publicUrl };
    } catch (error: any) {
      console.error('Upload profile picture error:', error);
      return { success: false, error: error.message };
    }
  },
};

/**
 * ================================================================
 * EXPORT ALL SERVICES
 * ================================================================
 */
export const masterBackend = {
  auth: authBackend,
  campaign: campaignBackend,
  application: applicationBackend,
  invitation: invitationBackend,
  discovery: discoveryBackend,
  analytics: analyticsBackend,
  payment: paymentBackend,
  messaging: messagingBackend,
  settings: settingsBackend,
  dashboard: dashboardService,
  brand: brandService,
  creator: creatorService,
  agency: agencyService,
};

export default masterBackend;
